import { PlusCircleFilled } from '@ant-design/icons';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import { type IAdminCategory } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Button,
  Col,
  Form,
  Row,
  Skeleton,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import SelectCategoryOptions from '../../fragments/select-category-options';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  categoriesLoadAll,
  getLikelyProductCategories,
} from '../../reducers/categories/reducer';
import { saveCategories } from '../../reducers/product/reducer';
import { SPACING } from '../../theme/tokens';
import FormSubmitFooter from '../form-submit-footer';
import Link from '../link';

interface IForm {
  categoryUUIDs: string[];
}

const { Title } = Typography;

function CategoriesComponent(): React.ReactElement {
  const dispatch = useAppDispatch();
  const categoriesAll = useSelector((s: RootState) => s.Categories.categories);
  const likelyProductCategories = useSelector(
    (s: RootState) => s.Categories.likelyProductCategories,
  );
  const categoriesUpdatedAt = useSelector(
    (s: RootState) => s.Categories.updatedAt,
  );
  const productUUID = useSelector((s: RootState) => s.Product.uuid);
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);
  const productUpdatedAt = useSelector((s: RootState) => s.Product.updatedAt);
  const productCategoryUUIDs = useSelector(
    (s: RootState) => s.Product.categories.uuids,
  );
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const [form] = Form.useForm<IForm>();

  const [categoriesEls, setCategoriesEls] = useState<
    React.ReactElement[] | null
  >(null);
  const [likelyProductCategoriesEls, setLikelyProductCategoriesEls] = useState<
    React.ReactElement[] | null
  >(null);
  const [likelyProductCategoriesChanged, setLikelyProductCategoriesChanged] =
    useState<number>(0);

  useEffect((): void => {
    form.setFieldsValue({
      categoryUUIDs: productCategoryUUIDs,
    });

    const categoriesElsUE: React.ReactElement[] = productCategoryUUIDs
      .map((categoryUUID: string): React.ReactElement | undefined => {
        const category: IAdminCategory | undefined = categoriesAll?.find(
          (c: IAdminCategory): boolean => c.uuid === categoryUUID,
        );

        if (!category) {
          return;
        }

        const categoryNameLocalized: string | null = getTranslation(
          category?.names,
          languageType,
        );

        return (
          <div key={categoryUUID}>
            <Link
              to={`/category/${category.uuid}`}
              label={categoryNameLocalized || EMPTY_VALUE}
              openInNewTab
            />
          </div>
        );
      })
      .filter(notEmpty);

    setCategoriesEls(categoriesElsUE);
  }, [
    form,
    productCategoryUUIDs,
    categoriesAll,
    languageType,
    productUpdatedAt,
  ]);

  useEffect((): void => {
    const categories: IAdminCategory[] | undefined = categoriesAll?.filter(
      (category: IAdminCategory): boolean =>
        !!likelyProductCategories.uuids?.includes(category.uuid),
    );

    if (categories === undefined || categories.length === 0) {
      return;
    }

    const actualCategoriesUUIDs: string[] = form.getFieldValue('categoryUUIDs');

    const likelyProductCategoriesElsUE: React.ReactElement[] = categories
      .filter(
        (category: IAdminCategory): boolean =>
          actualCategoriesUUIDs &&
          !actualCategoriesUUIDs.includes(category.uuid),
      )
      .sort(
        (a: IAdminCategory, b: IAdminCategory): number =>
          a.position - b.position,
      )
      .map((category: IAdminCategory): React.ReactElement => {
        const categoryName: string | null = getTranslation(
          category.names,
          languageType,
        );

        return (
          <div key={category.uuid}>
            <Space size={SPACING.lg} align="center">
              <Tooltip title="Add category">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<PlusCircleFilled />}
                  onClick={(): void => {
                    form.setFieldsValue({
                      categoryUUIDs: [...actualCategoriesUUIDs, category.uuid],
                    });

                    setLikelyProductCategoriesChanged(new Date().getTime());
                  }}
                />
              </Tooltip>

              <Link
                to={`/category/${category.uuid}`}
                openInNewTab
                label={categoryName || EMPTY_VALUE}
              />
            </Space>
          </div>
        );
      });

    setLikelyProductCategoriesEls(likelyProductCategoriesElsUE);
  }, [
    likelyProductCategories.uuids,
    categoriesUpdatedAt,
    languageType,
    form,
    likelyProductCategoriesChanged,
  ]);

  const onFinish = useCallback(
    ({ categoryUUIDs }: IForm): void => {
      dispatch(saveCategories(productUUID, categoryUUIDs));
    },
    [dispatch, productUUID],
  );

  useEffect((): void => {
    dispatch(categoriesLoadAll());
  }, [dispatch]);

  useEffect((): void => {
    if (productUUID) {
      dispatch(getLikelyProductCategories(productCategoryUUIDs));
    }
  }, [dispatch, productUUID]);

  return (
    <>
      <Title level={4} style={{ margin: 0 }}>
        Categories ({productCategoryUUIDs.length})
      </Title>

      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          categoryUUIDs: [],
        }}
      >
        <Row>
          <Col span={11}>
            <Form.Item
              name="categoryUUIDs"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <SelectCategoryOptions
                form={form}
                fieldName="categoryUUIDs"
                isDisabled={productInProcess}
                mode="multiple"
              />
            </Form.Item>

            {likelyProductCategories.uuids === undefined && <Skeleton active />}

            {likelyProductCategories.uuids?.length && (
              <div>{likelyProductCategoriesEls}</div>
            )}
          </Col>

          <Col span={11} offset={2}>
            <div>{categoriesEls}</div>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <FormSubmitFooter
              inProcess={productInProcess}
              updatedAt={productUpdatedAt}
              block
            />
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default React.memo(CategoriesComponent);
